import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import {SingleOrMultiSegment} from 'common/widgets/query-builder/types';
import {
  isEventBasedType,
  isGroupedEntry,
  isUserBasedType,
  QueryEntry,
  QuerySegmentEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {
  getSegmentType,
  isEventSegment,
  isMultiGroupBy,
  isNonEmptySegmentSelection,
  isUserSegment,
  Segment,
} from 'common/widgets/query-block/segment-menu/types';

/**
 * Sanitizes exclusionSteps by fixing out of range step selections
 */
export function sanitizeExclusionSteps(exclusionSteps: QueryEntry[], events: QueryEntry[]): QueryEntry[] {
  return exclusionSteps.map((step) => {
    if (`to` in step.excludeRange && step.excludeRange.to > events.length) {
      step.excludeAll = true;
      step.excludeRange = {};
    }

    return step;
  });
}

// The format used by mp-query-builder and mp-query-entry-section are different. So it's necessary to map
// between these two formats.
export function transformToQuerySegmentEntries(groupBy: Segment[][]): QuerySegmentEntry[] {
  return cloneDeep(groupBy).map((groupByItem: Segment[]) => {
    let customBucket;
    const segment = groupByItem[0];
    if (isEventSegment(segment) || isUserSegment(segment)) {
      const segmentation = Object.values(segment)[0];
      if (segmentation.customBucket) {
        // The QuerySegmentEntry format has the `customBucket` as a top-level field. So we remove `customBucket`
        // from the segment and move it to be a top-level field.
        customBucket = segmentation.customBucket;
        delete segmentation.customBucket;
      }
    }
    return {
      event: groupByItem,
      filters: [],
      type: SelectorTypeDefinitions.Segment,
      ...(customBucket ? {customBucket} : {}),
    };
  });
}

export function transformFromQuerySegmentEntries(entries: QuerySegmentEntry[]): Segment[][] {
  return entries.map((entry: QuerySegmentEntry) => {
    return produce(entry.event, (segment) => {
      if (entry.customBucket) {
        Object.values(segment[0])[0].customBucket = entry.customBucket;
      }
    });
  });
}

/**
 * Sanitizes group-by entries when a cohort is selected
 */
export function sanitizeGroupBys(events: QueryEntry[], segments: SingleOrMultiSegment) {
  const eventsOnlyEntries = events.every((entry) => isEventBasedType(entry.type) || isGroupedEntry(entry));
  const peopleOnlyEntries = events.length > 0 && events.every((entry) => isUserBasedType(entry.type));

  let updatedSegments;
  if (isMultiGroupBy(segments)) {
    updatedSegments = segments.filter((segment) => isNonEmptySegmentSelection(segment));
  } else {
    updatedSegments = [...segments] as Segment[];
  }

  if (peopleOnlyEntries) {
    const allowedTypes = [ResourceType.User, ResourceType.Cohort];
    updatedSegments = updatedSegments.filter((segment) => allowedTypes.includes(getSegmentType(segment)));
  } else if (!eventsOnlyEntries) {
    updatedSegments = updatedSegments.filter((segment) => getSegmentType(segment) === ResourceType.Cohort);
  }
  return updatedSegments;
}

/** TODO(pablo): drop this method once internal testing is completed */
export const useNewMetricsMode = (_projectId) => ({
  useNewMetricsMode: true,
  selectorTheme: `select`,
  selectorType: `button`,
  buttonTheme: `select`,
});
